// Pulse keyframes used by badges, dots, and primary button.
// Mount once near the app root (e.g. in App.js) so every page gets them.

import React from "react";

export const THEME_VARS = `
  /* ── Cyber theme (deep-navy glassmorphism, neon blue/purple) ──
     Variable NAMES are unchanged so every page re-skins automatically;
     only the values changed. --t-page stays a SOLID color (it is used as
     an SVG/canvas fill in Infrastructure, which cannot take a gradient).
     The radial neon glow lives in --t-page-glow, layered by body + Layout. */
  :root, [data-theme="dark"] {
    --badge-blue:    #93c5fd;
    --badge-amber:   #fde68a;
    --badge-orange:  #fb923c;
    --badge-green:   #6ee7b7;
    --badge-red:     #fca5a5;
    --badge-pink:    #fcd34d;
    --badge-gray:    #94a3b8;
    --badge-violet:  #c4b5fd;
    --t-page:         #050b1a;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(79,111,255,0.22), transparent 40%), radial-gradient(circle at 88% 8%, rgba(123,97,255,0.12), transparent 38%);
    --t-panel:        #0d1a36;
    --t-panel-hi:     #14264a;
    --t-input:        #122340;
    --t-input-hi:     #1a2e52;
    --t-card-input:   #173158;
    --t-card-head:    #101f3e;
    --t-surface-mid:  #1f3457;
    --t-surface-deep: #0a1530;
    --t-border-soft:   rgba(96,124,255,0.26);
    --t-border-softer: rgba(96,124,255,0.15);
    --t-border-mid:    rgba(96,124,255,0.38);
    --t-border-strong: rgba(96,124,255,0.55);
    --t-text-primary: #f4f8ff;
    --t-text-body:    #cdd9ef;
    --t-text-muted:   #93a6c9;
    --t-text-label:   #7188ad;
    --t-text-faint:   #5d6f93;
    --t-text-deep:    #46587a;
    --t-shadow-panel:    0 8px 26px rgba(0,0,0,0.45), 0 0 0 1px rgba(96,124,255,0.05), 0 0 18px rgba(79,111,255,0.07), inset 0 1px 0 rgba(255,255,255,0.05);
    --t-shadow-panel-hi: 0 12px 34px rgba(0,0,0,0.5), 0 0 26px rgba(79,111,255,0.16), inset 0 1px 0 rgba(255,255,255,0.06);
    --t-grad-sidebar:    linear-gradient(180deg,#0a1326 0%,#070e1d 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(22,38,69,0.9),rgba(16,29,56,0.65));
    --t-grad-list-head:  linear-gradient(180deg,rgba(16,29,56,0.9),rgba(16,29,56,0.55));
    --t-compose-bg:      rgba(16,29,56,0.96);
    --t-note-bg:         rgba(30,16,0,0.9);
    --t-note-bdr:        #78350f;
  }
  [data-theme="light"] {
    --badge-blue:    #1d4ed8;
    --badge-amber:   #92400e;
    --badge-orange:  #9a3412;
    --badge-green:   #065f46;
    --badge-red:     #991b1b;
    --badge-pink:    #92400e;
    --badge-gray:    #475569;
    --badge-violet:  #5b21b6;
    --t-page:         #f4f7fc;
    --t-page-glow:    radial-gradient(circle at 12% -5%, rgba(79,111,255,0.07), transparent 44%);
    --t-panel:        #ffffff;
    --t-panel-hi:     #f7f9fe;
    --t-input:        #ffffff;
    --t-input-hi:     #eef3fb;
    --t-card-input:   #eef4ff;
    --t-card-head:    #eef3fb;
    --t-surface-mid:  #e6edf8;
    --t-surface-deep: #dae3f1;
    --t-border-soft:   rgba(60,90,200,0.18);
    --t-border-softer: rgba(60,90,200,0.10);
    --t-border-mid:    rgba(60,90,200,0.30);
    --t-border-strong: rgba(60,90,200,0.48);
    --t-text-primary: #0b1428;
    --t-text-body:    #1e293b;
    --t-text-muted:   #475569;
    --t-text-label:   #5a6b8c;
    --t-text-faint:   #7a8fa5;
    --t-text-deep:    #9aafc4;
    --t-shadow-panel:    0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.9);
    --t-shadow-panel-hi: 0 4px 20px rgba(0,0,0,0.09), 0 2px 6px rgba(0,0,0,0.07), inset 0 1px 0 rgba(255,255,255,0.9);
    --t-grad-sidebar:    linear-gradient(180deg,#0b1c34 0%,#081428 100%);
    --t-grad-panel-head: linear-gradient(180deg,rgba(255,255,255,1),rgba(247,249,252,0.95));
    --t-grad-list-head:  linear-gradient(180deg,rgba(240,244,248,1),rgba(240,244,248,0.8));
    --t-compose-bg:      #f7f9fc;
    --t-note-bg:         rgba(251,191,36,0.06);
    --t-note-bdr:        rgba(180,83,9,0.4);
  }
`;

export const KEYFRAMES = `
  @keyframes badge-blue   { 0%,100%{box-shadow:0 0 0 0 rgba(96,165,250,0.30)}  50%{box-shadow:0 0 0 3px rgba(96,165,250,0)} }
  @keyframes badge-amber  { 0%,100%{box-shadow:0 0 0 0 rgba(251,191,36,0.30)}  50%{box-shadow:0 0 0 3px rgba(251,191,36,0)} }
  @keyframes badge-red    { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.40)}   50%{box-shadow:0 0 0 3px rgba(239,68,68,0)} }
  @keyframes badge-orange { 0%,100%{box-shadow:0 0 0 0 rgba(249,115,22,0.30)}  50%{box-shadow:0 0 0 3px rgba(249,115,22,0)} }
  @keyframes badge-green  { 0%,100%{box-shadow:0 0 0 0 rgba(52,211,153,0.25)}  50%{box-shadow:0 0 0 3px rgba(52,211,153,0)} }
  @keyframes dot-pulse    { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(0.85)} }
  @keyframes spin         { to { transform: rotate(360deg); } }
  @keyframes fade-in      { from { opacity:0; transform: translateY(4px); } to { opacity:1; transform: translateY(0); } }
`;

export function ThemeStyles() {
  return <style>{THEME_VARS + KEYFRAMES}</style>;
}
